/* eslint-disable max-lines-per-function */
/* eslint-disable camelcase */
/* eslint-disable new-cap */
import {TemplateAPIClient} from 'extensions/template-api-client';
import 'extensions/ui-components/x-address';
import 'extensions/ui-components/x-address-form';
import 'extensions/ui-components/x-loader';
import 'extensions/ui-components/x-message';
import {IntersectionObserverMixin} from 'extensions/ui-mixins';
import {FormElementMixin} from 'extensions/ui-mixins/form-element/lib/form-element-mixin';
import {defineCustomElement, isPoBox} from 'extensions/ui-utils';
import {LitElement, html, nothing} from 'lit';
import {createRef, ref} from 'lit/directives/ref.js';
import stylesheet from './x-address-list-input.scss?as=stylesheet';

export class AddressListInput extends FormElementMixin(IntersectionObserverMixin(LitElement)) {
	static get styles () {
		return [stylesheet];
	}

	static get properties () {
		return {
			_addressList: {
				state: true,
				type: Object
			},
			_errorMessage: {
				state: true,
				type: String
			},
			_selectedAddressId: {
				state: true,
				type: Number
			},
			_showAddressForm: {
				state: true,
				type: Boolean
			},
			address: {
				type: Object
			},
			customer: {
				type: Number
			}
		};
	}

	/**
	 * @type {import('extensions/ui-components/types').MivaCustomerAddress}
	 */
	get selectedAddress () {
		return Object.keys(this.address).length === 0
			? this._addressList?.find(address => address.id === this._selectedAddressId)
			: this.address;
	}

	get validationMessage () {
		if (super.validationMessage) {
			return 'There was an issue with your selected address. Please either use a different address or contact customer support to resolve the issue.';
		}

		return super.validationMessage;
	}

	/**
	 * @type {TemplateAPIClient}
	 */
	templateApiClient = new TemplateAPIClient();

	/**
	 * @type {import('lit/directives/ref.js').Ref<HTMLFormElement>}
	 */
	formRef = createRef();

	constructor () {
		super();

		/**
		 * @type {import('extensions/ui-components/types').MivaCustomerAddress[]}
		 */
		this._addressList = [];

		/**
		 * @type {string}
		 */
		this._errorMessage = '';

		/**
		 * @type {number}
		 */
		this._selectedAddressId = 0;

		/**
		 * @type {boolean}
		 */
		this._showAddressForm = false;

		/**
		 * @type {import('extensions/ui-components/types').MivaCustomerAddress}
		 */
		this.address = {};

		/**
		 * @type {number}
		 */
		this.customer = 0;
	}

	handleIntersection () {
		this.getAddressList();
	}

	async getAddressList () {
		const response = await this.templateApiClient.fetch('CUSTOMER_ADDRESS_LIST_LOAD_ALL', {
			Customer_ID: this.customer
		});

		if (!response.success) {
			this._errorMessage = `${response.error_code}: ${response.message}`;
		}

		this._addressList = response.data.filter(address => this.filterAddressList(address));

		const addressId = parseInt(this.address?.id, 10);
		this._selectedAddressId = isNaN(addressId) || addressId === void 0
			? parseInt(this._selectedAddressId, 10) || parseInt(this._addressList?.[0]?.id, 10) || 0
			: addressId;

		if (this._selectedAddressId === 0) {
			this._showAddressForm = true;
		}
	}

	/**
	 *
	 * @param {import('extensions/ui-components/types').MivaCustomerAddress} address
	 */
	filterAddressList (address) {
		// Filter if adddress is a post office box
		return !isPoBox(address.descrip) && !isPoBox(address.addr1) && !isPoBox(address.addr2);
	}

	handleAddressChange (e) {
		const [target] = e.composedPath();
		const value = parseInt(target.value, 10);

		this._selectedAddressId = value;
		this._showAddressForm = value === 0;

		if (value !== 0) {
			this.address = {};
		}

		this.dispatchEvent(new CustomEvent('change', {
			bubbles: true,
			composed: true
		}));
	}

	handleNewAddressClick (e) {
		e.preventDefault();

		this._selectedAddressId = this._showAddressForm
			? this._addressList?.[0]?.id
			: 0;
		this._showAddressForm = !this._showAddressForm;
		this.address = {};
	}

	renderSelectedAddress () {
		if (!this._selectedAddressId) {
			return nothing;
		}

		return html`
			<x-address class="address">
				<div slot="first-name">${this.selectedAddress?.fname}</div>
				<div slot="last-name">${this.selectedAddress?.lname}</div>

				<div slot="address">${this.selectedAddress?.addr1}</div>
				${this.selectedAddress?.addr2 ? html`<div slot="address">${this.selectedAddress?.addr2}</div>` : nothing}
				<div slot="city">${this.selectedAddress?.city}</div>
				<div slot="state">${this.selectedAddress?.state}</div>
				<div slot="zip">${this.selectedAddress?.zip}</div>
				<div slot="country">${this.selectedAddress?.cntry}</div>
			</x-address>
		`;
	}

	render () {
		if (!this.intersected || this._addressList?.length === 0) {
			return html`<x-loader aspect-ratio-desktop="730/116" aspect-ratio-tablet="750/190" aspect-ratio="750/190"></x-loader>`;
		}

		if (this._errorMessage) {
			return html`<x-message type="error">${this._errorMessage}</x-message>`;
		}

		return html`
			<form ${ref(this.formRef)} class="address-form" @change="${this.handleFormChange}" @submit="${this.handleFormSubmit}" class="container">
				<div class="address-select-container">
					<div class="c-form-select">
						<select class="c-form-select__dropdown" name="id" id="address-select" @change="${this.handleAddressChange}">
							<option ?selected="${this._selectedAddressId === 0 || this._selectedAddressId === '0'}" value="0">&lt;New&gt;</option>
							${this._addressList?.map(address => html`<option ?selected="${address.id === this._selectedAddressId}" value="${address.id}">${address.fname} ${address.lname}, ${address.addr}, ${address.city}, ${address.state} ${address.zip}, ${address.cntry}</option>`)}
						</select>
					</div>

					<div class="selected-address">
						${this.renderSelectedAddress()}
					</div>
				</div>
				<details .open="${this._showAddressForm}" class="add-new-address">
					<summary @click="${this.handleNewAddressClick}">Add New Address</summary>

					<div class="address-form-container">
						<x-address-form
							name="address-form"
							tabindex="0"
							?allow-other-state="${this._selectedAddressId >= 0}"
							fname="${this.selectedAddress?.fname}"
							lname="${this.selectedAddress?.lname}"
							email="${this.selectedAddress?.email}"
							comp="${this.selectedAddress?.comp}"
							phone="${this.selectedAddress?.phone}"
							fax="${this.selectedAddress?.fax}"
							addr1="${this.selectedAddress?.addr1}"
							addr2="${this.selectedAddress?.addr2}"
							city="${this.selectedAddress?.city}"
							state="${this.selectedAddress?.state}"
							zip="${this.selectedAddress?.zip}"
							cntry="${this.selectedAddress?.cntry}"
						></x-address-form>
					</div>
				</details>
			</form>
		`;
	}
};

defineCustomElement('x-address-list-input', AddressListInput);
